import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "./apiConfig";
import { Colors } from "./branding";
import { useTheme, getBrandColors} from "./ThemeContext";
import { FaChartLine } from "react-icons/fa";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useSort } from "./hooks/useSort";

const C = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; } };
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FONT = "'Segoe UI', system-ui, sans-serif";

const fmtAmt = v => `$${(Number(v) || 0).toFixed(2)}`;
const fmtHrs = m => { const h = Math.floor((Number(m)||0)/60); const min=(Number(m)||0)%60; return min?`${h}h ${min}m`:`${h}h`; };

export default function OTOFinancials() {
  const { isDark: T } = useTheme();
  const textPrimary   = T ? "rgba(255,255,255,0.88)" : "#1a2a3a";
  const textSecondary = T ? "rgba(255,255,255,0.50)" : "rgba(3,78,123,0.55)";
  const divider       = T ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)";

  const card = {
    background: T ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.90)",
    border: T ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(3,78,123,0.12)",
    borderRadius: 14, padding: "18px 22px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
  };
  const inputSt = {
    padding: "7px 11px", borderRadius: 8, fontSize: 13, outline: "none",
    background: T ? "rgba(255,255,255,0.08)" : "#f4f7fb",
    color: T ? "rgba(255,255,255,0.88)" : "#1a2a3a",
    border: T ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(3,78,123,0.18)",
  };
  const thSt = {
    padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", color: textSecondary,
    borderBottom: `2px solid ${divider}`, whiteSpace: "nowrap",
  };
  const tdSt = { padding: "10px 12px", borderBottom: `1px solid ${divider}` };

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [data,  setData]  = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/oto/admin/dashboard?month=${month}&year=${year}`);
      const d = await res.json();
      if (d.success) setData(d);
    } catch {}
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const totals  = data?.totals  || {};
  const coaches = data?.coaches || [];
  const { sorted: sortedCoaches, toggleSort: toggleCoachSort, sortArrow: coachSortArrow } = useSort(coaches, "coach_name");
  const totalCoachCost  = coaches.reduce((s, c) => s + (Number(c.coach_cost)  || 0), 0);
  const totalNetRevenue = coaches.reduce((s, c) => s + (Number(c.net_revenue) || 0), 0);
  const totalHours      = coaches.reduce((s, c) => s + (Number(c.hours_delivered) || 0), 0);
  const totalCheckIns   = coaches.reduce((s, c) => s + (Number(c.checkedin_sessions) || 0), 0);
  const maxRevenue      = Math.max(...coaches.map(c => Number(c.revenue) || 0), 1);

  return (
    <div style={{ fontFamily: FONT, color: textPrimary, padding: "28px 32px", maxWidth: 1300 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <PageHeaderIcon icon={<FaChartLine size={20} color={getBrandColors().blueDark} />} />
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: T ? "#93c5fd" : C.blue }}>
            1-on-1 Financials
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={inputSt}>
            {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={inputSt}>
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={load}
            style={{ padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                     fontSize: 12, fontWeight: 700, background: C.blue, color: "#fff" }}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: textSecondary, textAlign: "center", padding: "60px 0" }}>Loading…</div>
      ) : (
        <>
          {/* KPI Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Gross Revenue",   value: fmtAmt(totals.total_revenue), color: C.yellow,   note: "Billed to parents" },
              { label: "Coach Cost",      value: fmtAmt(totalCoachCost),        color: getBrandColors().red,  note: "Pay to coaches" },
              { label: "Net Revenue",     value: fmtAmt(totalNetRevenue),       color: getBrandColors().green,  note: "Sixers margin" },
              { label: "Total Sessions",  value: totals.total_bookings ?? 0,    color: textPrimary },
              { label: "Check-ins",       value: totalCheckIns,                 color: "#60a5fa",  note: "Physically attended" },
              { label: "Hours Delivered", value: `${totalHours.toFixed(1)}h`,   color: textPrimary },
            ].map((k, i) => (
              <div key={i} style={{ ...card, textAlign: "center", padding: "14px 16px" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: textSecondary, marginTop: 4,
                  letterSpacing: "0.06em", textTransform: "uppercase" }}>{k.label}</div>
                {k.note && <div style={{ fontSize: 10, color: textSecondary, marginTop: 2 }}>{k.note}</div>}
              </div>
            ))}
          </div>

          {/* Net Revenue formula */}
          <div style={{ ...card, marginBottom: 24, padding: "12px 20px",
            background: T ? "rgba(52,211,153,0.06)" : "rgba(52,211,153,0.06)",
            border: "1px solid rgba(52,211,153,0.20)" }}>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: getBrandColors().green }}>Net Revenue Formula:</span>
              <span style={{ fontSize: 12, color: textSecondary }}>
                <strong style={{ color: C.yellow }}>{fmtAmt(totals.total_revenue)}</strong> Gross Revenue
                &nbsp;−&nbsp;
                <strong style={{ color: getBrandColors().red }}>{fmtAmt(totalCoachCost)}</strong> Coach Pay
                &nbsp;=&nbsp;
                <strong style={{ color: getBrandColors().green }}>{fmtAmt(totalNetRevenue)}</strong> Sixers Net
              </span>
              <span style={{ fontSize: 11, color: textSecondary, marginLeft: "auto" }}>
                {MONTHS[month-1]} {year} · {coaches.filter(c => c.total_sessions > 0).length} active coach{coaches.filter(c=>c.total_sessions>0).length!==1?"es":""}
              </span>
            </div>
          </div>

          {/* Per-Coach Breakdown */}
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>
              Coach Breakdown — {MONTHS[month-1]} {year}
            </div>
            {coaches.length === 0 ? (
              <div style={{ color: textSecondary }}>No coaches found.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      {[
                        { label: "Coach", key: "coach_name" },
                        { label: "Billing Rate", key: "billing_rate" },
                        { label: "Pay Rate", key: "pay_rate" },
                        { label: "Sessions", key: "total_sessions" },
                        { label: "Check-ins", key: "checkedin_sessions" },
                        { label: "Completed", key: "completed_sessions" },
                        { label: "Hours", key: "hours_delivered" },
                        { label: "Gross Revenue", key: "revenue" },
                        { label: "Coach Pay", key: "coach_cost" },
                        { label: "Net Revenue", key: "net_revenue" },
                        { label: "Margin %", key: null },
                        { label: "", key: null },
                      ].map(h => (
                        <th key={h.label || "spacer"} onClick={h.key ? () => toggleCoachSort(h.key) : undefined}
                          style={{ ...thSt, ...(h.key ? { cursor: "pointer", userSelect: "none" } : {}) }}>
                          {h.label}{h.key ? coachSortArrow(h.key) : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCoaches.map((c, ri) => {
                      const gross  = Number(c.revenue)     || 0;
                      const cost   = Number(c.coach_cost)  || 0;
                      const net    = Number(c.net_revenue) || 0;
                      const margin = gross > 0 ? Math.round((net / gross) * 100) : 0;
                      const revPct = Math.round((gross / maxRevenue) * 100);
                      const rowBg  = ri%2===0 ? "transparent" : (T?"rgba(255,255,255,0.02)":"rgba(3,78,123,0.02)");
                      return (
                        <tr key={c.coach_id} style={{ background: rowBg }}>
                          <td style={{ ...tdSt, fontWeight: 700 }}>{c.coach_name}</td>
                          <td style={{ ...tdSt, color: C.yellow, fontWeight: 700 }}>
                            {fmtAmt(c.billing_rate)}<span style={{ fontSize: 10, color: textSecondary }}>/hr</span>
                          </td>
                          <td style={{ ...tdSt, color: getBrandColors().red, fontWeight: 700 }}>
                            {fmtAmt(c.pay_rate)}<span style={{ fontSize: 10, color: textSecondary }}>/hr</span>
                          </td>
                          <td style={{ ...tdSt, textAlign: "center" }}>{c.total_sessions}</td>
                          <td style={{ ...tdSt, textAlign: "center" }}>
                            <span style={{ fontWeight: 700, color: "#60a5fa" }}>{c.checkedin_sessions}</span>
                          </td>
                          <td style={{ ...tdSt, textAlign: "center" }}>{c.completed_sessions}</td>
                          <td style={tdSt}>
                            {Number(c.hours_delivered)>0 ? `${Number(c.hours_delivered).toFixed(1)}h` : fmtHrs(c.total_minutes)}
                          </td>
                          <td style={{ ...tdSt, fontWeight: 800, color: C.yellow }}>{fmtAmt(gross)}</td>
                          <td style={{ ...tdSt, color: getBrandColors().red, fontWeight: 700 }}>{fmtAmt(cost)}</td>
                          <td style={{ ...tdSt, fontWeight: 800, color: net>=0?getBrandColors().green:getBrandColors().red }}>{fmtAmt(net)}</td>
                          <td style={tdSt}>
                            <span style={{ fontSize: 12, fontWeight: 700,
                              color: margin>=50?getBrandColors().green:margin>=30?C.yellow:getBrandColors().red }}>{margin}%</span>
                          </td>
                          <td style={{ ...tdSt, minWidth: 80 }}>
                            <div style={{ height: 6, borderRadius: 3,
                              background: T?"rgba(255,255,255,0.10)":"rgba(3,78,123,0.10)", overflow: "hidden" }}>
                              <div style={{ height: "100%", width:`${revPct}%`,
                                background: C.yellow, borderRadius: 3, transition: "width 0.4s" }}/>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ background: T?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.04)", fontWeight: 800 }}>
                      <td style={{ ...tdSt, fontWeight: 900 }}>TOTAL</td>
                      <td style={tdSt}/><td style={tdSt}/>
                      <td style={{ ...tdSt, textAlign:"center" }}>{totals.total_bookings ?? 0}</td>
                      <td style={{ ...tdSt, textAlign:"center", color:"#60a5fa" }}>{totalCheckIns}</td>
                      <td style={{ ...tdSt, textAlign:"center" }}>{totals.completed ?? 0}</td>
                      <td style={tdSt}>{totalHours.toFixed(1)}h</td>
                      <td style={{ ...tdSt, color: C.yellow }}>{fmtAmt(totals.total_revenue)}</td>
                      <td style={{ ...tdSt, color:getBrandColors().red }}>{fmtAmt(totalCoachCost)}</td>
                      <td style={{ ...tdSt, color:getBrandColors().green }}>{fmtAmt(totalNetRevenue)}</td>
                      <td style={{ ...tdSt, color:getBrandColors().green }}>
                        {totals.total_revenue>0 ? `${Math.round((totalNetRevenue/Number(totals.total_revenue))*100)}%` : "—"}
                      </td>
                      <td style={tdSt}/>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
